import { TestCardStatus, TestType } from "@app/provider/testData/types";

export const GIS_RETURN_URL_KEY = "gis_return_url";
export const GIS_ENTRY_SOURCE_KEY = "gis_entry_source";
export const GIS_ENTRY_SOURCE_PUBLIC = "Public_Entry_UPP_Auth";
export const GIS_ORDER_MAX_AGE_MONTHS = 3;

/**
 * Order-lookup public funnel entry source (public flow User Stories 4–6).
 * Stored on `onc_upp_gis_triage.scheduling_source` for funnel attribution.
 */
export const GIS_ENTRY_SOURCE_ORDER_LOOKUP = "Public_Entry_Order_Lookup";

/**
 * Authenticated UPP test-result page entry source. Persisted on
 * `onc_upp_gis_triage.scheduling_source` when a patient launches the GIS
 * funnel from their test-result detail page.
 */
export const GIS_SCHEDULING_SOURCE_TEST_RESULT = "testResultDetailPage";

/**
 * Sentinel order/test uid used to route a patient with no GIS-eligible order
 * into the ONC intake flow. The confirm-info step detects this value and
 * renders the "we couldn't find a test order" messaging while still letting the
 * patient schedule a session.
 */
export const GIS_NO_ORDER_UID = "no-order";

export type CounselingIntent = "pre-test" | "post-test";

export function classifyCounselingIntent(
  status: TestCardStatus | string | undefined
): CounselingIntent {
  if (
    status === TestCardStatus.RESULT_READY ||
    status === TestCardStatus.TEST_COMPLETE
  ) {
    return "post-test";
  }
  return "pre-test";
}

const GIS_EXCLUDED_STATUSES = new Set<string>([
  TestCardStatus.CANCELLED_TEST,
  TestCardStatus.EXPIRED_TEST,
]);

export function isGisEligibleStatus(status: TestCardStatus | string): boolean {
  return !GIS_EXCLUDED_STATUSES.has(status);
}

/**
 * Determines whether a product (test) type is eligible to appear in the public
 * GIS funnel (the order picker and the eligible-order count).
 *
 * PROSPERA (Organ Health transplant assessment) is not offered a Genetic
 * Information Session — it has no Acuity appointment-type mapping and no
 * Renasight-style external scheduling page — so it is excluded. A patient whose
 * only order is PROSPERA is treated as having no GIS-eligible order and routes
 * through the no-order (pre-test) intake flow instead of dead-ending.
 *
 * RENASIGHT (Organ Health kidney panel) *is* offered a GIS via the
 * Renasight-specific scheduling page (`getGisScheduleLink`), so it remains
 * eligible: it shows up in the multi-order picker alongside oncology/WH tests,
 * and triage routes a selected Renasight order to that external scheduling
 * page.
 */
export function isGisEligibleTestType(
  testType: TestType | string | undefined
): boolean {
  if (!testType) return false;
  return testType !== TestType.PROSPERA;
}

/**
 * Orders with missing/invalid creation dates are excluded because their age
 * cannot be verified.
 */
export function isWithinGisRecencyWindow(
  createdAt: Date | string | null | undefined
): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - GIS_ORDER_MAX_AGE_MONTHS);
  return created >= cutoff.getTime();
}

export function storeGisReturnUrl(url: string): void {
  try {
    sessionStorage.setItem(GIS_RETURN_URL_KEY, url);
  } catch {
    // Ignore storage errors
  }
}

export function getGisReturnUrl(): string | null {
  try {
    return sessionStorage.getItem(GIS_RETURN_URL_KEY);
  } catch {
    return null;
  }
}

export function clearGisReturnUrl(): void {
  try {
    sessionStorage.removeItem(GIS_RETURN_URL_KEY);
  } catch {
    // Ignore
  }
}

export function storeGisEntrySource(source: string): void {
  try {
    sessionStorage.setItem(GIS_ENTRY_SOURCE_KEY, source);
  } catch {
    // Ignore storage errors
  }
}

export function getGisEntrySource(): string | null {
  try {
    return sessionStorage.getItem(GIS_ENTRY_SOURCE_KEY);
  } catch {
    return null;
  }
}

export function clearGisEntrySource(): void {
  try {
    sessionStorage.removeItem(GIS_ENTRY_SOURCE_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Validates that a return URL is internal (same-origin, relative path)
 * to prevent open-redirect vulnerabilities.
 */
export function isValidReturnUrl(url: string): boolean {
  if (!url) return false;
  try {
    if (url.startsWith("/") && !url.startsWith("//")) {
      return true;
    }
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}
